import { action, computed, observable } from 'mobx';
import { TechOverviewCollateral, TechOverview } from 'types/techOverview';
import { VocabularyNamespaces, VocabularyItem } from 'types';
import VocabularyStore from 'stores/Vocabulary';
import ProgressStore from 'stores/Progress';
import CatalogStore from 'stores/Catalog';
import UIStore from 'stores/UI';
import { extractFromSlug } from 'helpers/utils';

import {
  getTechOverviewCollateral,
  getTechOverview,
} from 'services/TechOverviewService';

class TechOverviewOverviewStore {
  @observable techOverview: TechOverview;

  @observable techOverviewCollateral: TechOverviewCollateral;

  @observable isLoading: boolean = true;

  @observable slug: string;

  @observable versions: string[];

  @observable translations: string[];

  @observable progress = null;

  @observable productVocabulary: VocabularyItem[] = null;

  @observable audienceVocabulary: VocabularyItem[] = null;

  @observable topicVocabulary: VocabularyItem[] = null;

  progressStore: ProgressStore;

  vocabularyStore: VocabularyStore;

  catalogStore: CatalogStore;

  uiStore: UIStore;

  constructor(
    slug: string,
    progressStore: ProgressStore,
    vocabularyStore: VocabularyStore,
    catalogStore: CatalogStore,
    uiStore: UIStore,
  ) {
    this.slug = slug;
    this.progressStore = progressStore;
    this.vocabularyStore = vocabularyStore;
    this.catalogStore = catalogStore;
    this.uiStore = uiStore;
  }

  static stripHtml = (body: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = body;
    return temp.textContent || '';
  };

  @computed get currentVersion() {
    const { version } = extractFromSlug(this.slug);
    return version;
  }

  @computed get description() {
    return this.techOverviewCollateral
      ? TechOverviewOverviewStore.stripHtml(
          this.techOverviewCollateral.overview.body,
        )
      : null;
  }

  @computed get prerequisites() {
    if (!this.techOverviewCollateral?.prerequisites?.body) {
      return [];
    }

    const text = TechOverviewOverviewStore.stripHtml(
      this.techOverviewCollateral.prerequisites.body,
    );

    return text
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  @computed get products() {
    return this.techOverview ? this.techOverview.taxonomies.products : null;
  }

  @computed get productLabels() {
    if (
      this.techOverview &&
      this.techOverview.taxonomies?.products &&
      this.productVocabulary
    ) {
      const labels = this.techOverview.taxonomies.products.flatMap((p) => {
        const vocabLabel = this.productVocabulary.find((v) => v.token === p);
        return vocabLabel ? [vocabLabel.display_name] : [];
      });

      return labels;
    }

    return null;
  }

  @computed get topicLabels() {
    if (
      this.techOverview &&
      this.techOverview.taxonomies?.topics &&
      this.topicVocabulary
    ) {
      const labels = this.techOverview.taxonomies.topics.flatMap((p) => {
        const vocabLabel = this.topicVocabulary.find((v) => v.token === p);
        return vocabLabel ? [vocabLabel.display_name] : [];
      });

      return labels;
    }

    return null;
  }

  @computed get audienceLabels() {
    if (
      this.techOverview &&
      this.techOverview.taxonomies?.audiences &&
      this.audienceVocabulary
    ) {
      const labels = this.techOverview.taxonomies.audiences.flatMap((p) => {
        const vocabLabel = this.audienceVocabulary.find((v) => v.token === p);
        return vocabLabel ? [vocabLabel.display_name] : [];
      });

      return labels;
    }

    return null;
  }

  @computed get topics() {
    return this.techOverview ? this.techOverview.taxonomies.topics : null;
  }

  @computed get audiences() {
    return this.techOverview ? this.techOverview.taxonomies.audiences : null;
  }

  @computed get title() {
    return this.techOverview ? this.techOverview.title : null;
  }

  @action fetchOverviewData = async (slug: string, language: string) => {
    this.slug = slug;
    await this.getTechOverview(slug);
    await this.getTechOverviewCollateral(slug, language);
    this.getProgress(slug);
    this.getVersions(slug);
    this.getTranslations(slug);

    const productVocabulary =
      await this.vocabularyStore.getVocabularyByNamespace(
        VocabularyNamespaces.OfferingTaxonomyProducts,
        this.uiStore.currentLanguage,
      );
    if (productVocabulary && productVocabulary.length) {
      this.productVocabulary = productVocabulary;
    }

    const audienceVocabulary =
      await this.vocabularyStore.getVocabularyByNamespace(
        VocabularyNamespaces.OfferingTaxonomyAudiences,
        this.uiStore.currentLanguage,
      );
    if (audienceVocabulary && audienceVocabulary.length) {
      this.audienceVocabulary = audienceVocabulary;
    }

    const topicVocabulary = await this.vocabularyStore.getVocabularyByNamespace(
      VocabularyNamespaces.OfferingTaxonomyTopics,
      this.uiStore.currentLanguage,
    );
    if (topicVocabulary && topicVocabulary.length) {
      this.topicVocabulary = topicVocabulary;
    }

    this.isLoading = false;
  };

  @action getVersions(slug: string) {
    const { code } = extractFromSlug(slug);
    this.versions = this.catalogStore.getSortedVersions(code);
    return this.versions;
  }

  @action getTranslations(slug: string) {
    const { code } = extractFromSlug(slug);
    this.translations = this.catalogStore.getTranslations(code);
    return this.translations;
  }

  @action getProgress = (slug: string) => {
    this.progress = this.progressStore.getProgressForCourse(slug);
  };

  @action getTechOverview = async (slug: string) => {
    this.isLoading = true;
    try {
      const payload = await getTechOverview(slug);
      if (payload) {
        this.techOverview = payload;
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.isLoading = false;
    }
    return this.techOverview;
  };

  @action getTechOverviewCollateral = async (
    slug: string,
    language: string,
  ) => {
    const id = `${slug.replace('-', '@@')}@@${language}`;

    if (this.techOverviewCollateral?.doc_id === id) {
      return this.techOverviewCollateral;
    }

    const collateral = await getTechOverviewCollateral(id);
    if (collateral) {
      this.techOverviewCollateral = collateral;
    }

    return this.techOverviewCollateral;
  };
}

export default TechOverviewOverviewStore;
